import { Injectable } from '@angular/core';
import { Tag, PapCategoriesService } from '@isophi/pap-api';
import { Observable } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { HelperService } from '@app/core/services/helper.service';

@Injectable({
  providedIn: 'root',
})
export class CategoryService {
  private categories$: Observable<Tag[]>;

  public constructor(private categoriesApiService: PapCategoriesService, private helperService: HelperService) {}

  public getCategories(): Observable<Tag[]> {
    if (!this.categories$) {
      this.categories$ = this.categoriesApiService.papCategoriesList(undefined, undefined, undefined, 1000).pipe(
        map((response) =>
          response.results.map((tag) => {
            return {
              ...tag,
              activeIconUrl: this.helperService.getTagImageUrl(tag.activeIconUrl),
              inactiveIconUrl: this.helperService.getTagImageUrl(tag.inactiveIconUrl),
            };
          })
        ),
        shareReplay(1),
        catchError((error) => {
          console.log(error);
          throw new Error('Unable to download categories data.');
        })
      );
    }
    return this.categories$;
  }

  public getCategoryMap(): Observable<{ [uuid: string]: Tag }> {
    return this.getCategories().pipe(
      map((categories) => categories.reduce((categoryMap, category) => ({ ...categoryMap, [category.uuid]: category }), {}))
    );
  }

  /**
   * Get super categories (categories with no parent)
   */
  public getSuperCategories(): Observable<Tag[]> {
    return this.getCategories().pipe(
      map((categories) => categories.filter((category) => !category.parentId))
    );
  }

  /**
   * Get subcategories for a specific parent category
   */
  public getSubCategories(parentId: number): Observable<Tag[]> {
    return this.getCategories().pipe(
      map((categories) => categories.filter((category) => category.parentId === parentId))
    );
  }

  /**
   * Get all subcategories (categories with a parent)
   */
  public getAllSubCategories(): Observable<Tag[]> {
    return this.getCategories().pipe(
      map((categories) => categories.filter((category) => category.parentId !== null && category.parentId !== undefined))
    );
  }

  /**
   * Get category hierarchy with super categories and their subcategories
   */
  public getCategoryHierarchy(): Observable<{ superCategory: Tag; subCategories: Tag[] }[]> {
    return this.getCategories().pipe(
      map((categories) => {
        const superCategories = categories.filter((category) => !category.parentId);
        return superCategories.map((superCategory) => ({
          superCategory,
          subCategories: categories.filter((category) => category.parentId === superCategory.id)
        }));
      })
    );
  }
}

